/**
 * Copyright: ThoughtSpot Inc. 2020
 * Author: Thawsitt Naing (thawsitt.naing@thoughtspot.com)
 *
 * @fileoverview Banner Alert for Radiant
 */

import cx from 'classnames';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '../../button';
import { Icon, IconColor, IconID, IconSize } from '../../icon';
import { Typography } from '../../typography';
import { CLOSE_BTN_ARIA_LABEL } from '../alert-constants';
import { AlertActionButton, AlertLink } from '../alert-interface';
import { createActionLink } from '../alert-utils';
import styles from './banner-alert.module.scss';

export enum BannerAlertType {
  WARNING = 'warning',
  ERROR = 'error',
  SUCCESS = 'success',
  INFO = 'info',
}

export interface BannerAlertProps {
  /**
   * Alert message to show to the user
   */
  message: string | React.ReactElement;
  /**
   * Whether the alert is visible
   */
  isVisible: boolean;
  /**
   * Alert message type
   *
   * @default 'warning'
   */
  type?: BannerAlertType;
  /**
   * Whether the user can close the alert
   *
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Callback function for when the user clicks on close button
   *
   * @default noop
   */
  onClose?: () => void;
  /**
   * Show a link for the user to click
   *
   * @default undefined
   */
  link?: AlertLink;
  /**
   * Show a second link for the user to click
   *
   * @default undefined
   */
  secondaryLink?: AlertLink;
  /**
   * Show a primary button with callback
   */
  primaryAction?: AlertActionButton;

  /**
   * Show a secondary button with callback
   */
  secondaryAction?: AlertActionButton;
  /**
   * Whether the banner alert is a Global Alert (Page Level) or Local Alert (Section Level).
   *
   * Global banner alerts are center-aligned whereas local alerts are left-aligned.
   *
   * You don't need to set this manually. It is only used by global alert context provider.
   *
   * @default false
   */
  global?: boolean;
  /**
   * Any additional class name for alert container
   *
   * @default ''
   */
  className?: string;

  /**
   * Custom icons for the banner
   */
  customIconID?: IconID;

  /**
   * To make the alert accessible
   */
  shouldAnnounceAlert?: boolean;
  /**
   * flag to remove the global alert during unmount
   */
  closeOnUnmount?: boolean;
  /**
   * function to add mixpanel track
   */
  collectEvent?: (userAction: any, property: any) => void;
  /**
   * full error object
   */
  errorObj?: any;
  /**
   * aria-label for the alert
   */
  ariaLabel?: string;
  /**
   * tabIndex for the alert if it is clickable
   */
  isClickable?: boolean;
}

export const BannerAlert: React.FC<BannerAlertProps> = ({
  message,
  isVisible,
  type = BannerAlertType.WARNING,
  showCloseButton = true,
  onClose = _.noop,
  link,
  secondaryLink,
  primaryAction,
  secondaryAction,
  global = false,
  className = '',
  customIconID,
  shouldAnnounceAlert,
  closeOnUnmount,
  collectEvent,
  errorObj,
  ariaLabel,
  isClickable,
  ...restProps
}: BannerAlertProps) => {
  const [height, setHeight] = useState(0);
  /*
   * If content is multiline for Section Banner Alert (Local Alert), setting the height of container to add extra styling
   * on Icon, Button and Link.
   */
  const bannerRef = useCallback((element: HTMLDivElement) => {
    if (element) {
      setHeight(element.clientHeight);
    }
  }, []);

  const localOrGlobal = global === true ? 'global' : 'local';

  const multiLineAndLocal = height > 60 && !global; // To check if Alert is local and on multi-line.

  const colors = {
    [BannerAlertType.WARNING]: IconColor.YELLOW,
    [BannerAlertType.ERROR]: IconColor.RED,
    [BannerAlertType.SUCCESS]: IconColor.GREEN,
    [BannerAlertType.INFO]: IconColor.BLUE,
  };

  useEffect(() => {
    if (isVisible && type === BannerAlertType.ERROR) {
      const genericError = new Error(message as string);
      genericError.name = 'TSError';
      // eslint-disable-next-line no-console
      console.error(genericError);
    }
  }, [errorObj, global, isVisible, message, type, collectEvent]);

  const iconColor = colors[type];

  const renderLink = (actionLink?: AlertLink) => {
    const linkStyles = cx(styles.link, {
      [styles.linkInMultiline]: multiLineAndLocal,
    });

    return actionLink ? createActionLink(actionLink, linkStyles) : null;
  };

  const renderMessage = (messageBody: string | React.ReactElement) => {
    if (_.isString(messageBody)) {
      return (
        <div className={styles.contentSub}>
          <Typography variant="body-normal" noMargin>
            {messageBody}
          </Typography>
        </div>
      );
    }
    return <div className={styles.contentSub}>{messageBody}</div>;
  };

  const primaryActionButton = primaryAction ? (
    <Button
      text={primaryAction.label}
      type="primary"
      ariaLabel={primaryAction.label}
      size="s"
      backgroundType="colored"
      className={cx({
        [styles.primaryActionInMultiline]: multiLineAndLocal,
      })}
      onClick={primaryAction.onClick}
      isDisabled={primaryAction.isDisabled}
    />
  ) : null;

  const secondaryActionButton = secondaryAction ? (
    <Button
      text={secondaryAction.label}
      type="secondary"
      ariaLabel={secondaryAction.label}
      size="s"
      backgroundType="light"
      className={styles.secondaryActionInMultiline}
      onClick={secondaryAction.onClick}
    />
  ) : null;

  const containerClass = cx(styles.container, styles[type], styles[localOrGlobal], className, {
    [styles.noCloseButton]: !showCloseButton,
  });

  if (!isVisible || !message) {
    return null;
  }

  let iconID = IconID.EXCLAMATION_POINT_CIRCLE;
  if (customIconID) {
    iconID = customIconID;
  } else if (type === BannerAlertType.SUCCESS) {
    iconID = IconID.CHECKMARK_CIRCLE;
  }

  return (
    <div
      className={containerClass}
      {...restProps}
      ref={bannerRef}
      role="alert"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isClickable ? 0 : -1}
      aria-label={ariaLabel}
      {...(shouldAnnounceAlert
        ? {
            'aria-live': 'assertive',
            'aria-atomic': 'true',
          }
        : {})}
    >
      <Icon
        id={iconID}
        size={IconSize.LARGE}
        color={iconColor}
        className={cx(styles.icon, {
          [styles.iconInMultiline]: multiLineAndLocal,
        })}
      />
      <div className={styles.content}>
        {renderMessage(message)}
        {renderLink(link)}
        {renderLink(secondaryLink)}
        {primaryActionButton}
        {secondaryActionButton}
      </div>
      {showCloseButton && (
        <Button
          size={global ? 'm' : 's'}
          icon={IconID.CROSS}
          type="secondary"
          ariaLabel={CLOSE_BTN_ARIA_LABEL}
          onClick={onClose}
          className={styles.closeButton}
        />
      )}
    </div>
  );
};
